import React, { useState } from 'react';
// import PropTypes from 'prop-types';
import styled, { createGlobalStyle } from 'styled-components';
import axios from 'axios';
import { Flex, Box } from '@rebass/grid';
import { DynamicThemeProvider, MatchMedia, media } from '@amtrav/themes';
import { IconEmailLine } from '@amtrav/icons';
import ValidatorApi from '@amtrav/validator-api';
import { WizardTracker, Notice } from '@amtrav/view-components';
import { getTokenValue, THEME_TOKENS } from '@amtrav/theme-system';
import { CompanyForm, ProfileForm } from './AccountSetupForms';
import { bubbleMessage1, bubbleMessage2, introMessage1, introMessage2, amtravHome } from './consts';
import {
  UserAgreementLinks,
  CustomerServiceCTA,
  HeaderMobile,
  Sidebar,
  AccountSetupPage,
  SignUpContent,
} from '../../components';
import AccountSetupSuccess from './AccountSetupSuccess';
import StepHeader from './StepHeader';
import MarketingBubbles from './MarketingBubbles';

const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
  }
  html {
    background-color: ${getTokenValue(THEME_TOKENS.navigation.color.sectionBackground)};
  }
`;

const VisualStepsWrapper = styled(Box)`
  width: 100%;

  ${media.Large`
    width: 400px;
  `};
`;

const NumberText = styled(Box)`
  font-size: 18px;
  transform: translateY(-2px);
`;

const FormWrapper = styled(Box)`
  height: auto;

  ${media.Large`
    height: 360px;
  `};
`;

const IntroMessage = styled(Flex)`
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  align-items: flex-end;
  justify-content: center;
  width: 504px;
  height: 80px;
  font-size: 20px;
  text-align: center;
  line-height: 1.4;
  margin: 0 auto 80px auto;

  @media (max-height: 1000px) {
    align-items: center;
    margin: 0 auto 40px auto;
    font-size: 18px;
    height: 70px;
  }
`;

const Subhead = () => {
  return (
    <>
      It&apos;s Easy to Get Started <br />
      With AmTrav
    </>
  );
};

const companyFormInit = {
  companyName: '',
  companyAddress: '',
  companyWebsite: 'http://',
  companyIndustry: '',
  estimatedNumberOfTravelers: '',
  estimatedAnnualTravelBudget: '',
};
const profileFormInit = {
  email: '',
  firstName: '',
  middleName: '',
  lastName: '',
  suffixName: '',
  password: '',
  confirmPassword: '',
  jobTitle: '',
};

const validatorClient = axios.create({
  baseURL: `${window.location.origin}/api`,
});
const validator = new ValidatorApi({
  rulesEndpoint: '/v1/validation?route=/v2/Subscription&httpMethod=POST',
  axios: validatorClient,
});

const submitAccountData = (payload) => {
  return validatorClient.post('/v2/subscription', payload);
};

// Main App component

const AccountSetupApp = () => {
  // The current step
  const [step, setStep] = useState(0);

  // The two form components
  const [companyInfo, setCompanyInfo] = useState(companyFormInit);
  const [profileInfo, setProfileInfo] = useState(profileFormInit);

  // Any global errors
  const [subError, setSubError] = useState(null);

  // Controls for moving around the wizard
  const nextStep = () => {
    setStep((current) => current + 1);
  };
  const prevStep = () => {
    setStep((current) => current - 1);
  };

  function finishIcon(s) {
    if (s < 2) {
      return <NumberText>3</NumberText>;
    }
    return <IconEmailLine />;
  }

  // Wizard steps
  const steps = [
    {
      label: 'Your Company',
      additionalInfo: '',
      icon: <NumberText>1</NumberText>,
    },
    {
      label: 'Your Profile',
      additionalInfo: '',
      icon: <NumberText>2</NumberText>,
    },
    {
      label: 'Finish Up',
      additionalInfo: '',
      icon: finishIcon(step),
    },
  ];

  // Set up and select which form to show
  function selectForm(index) {
    switch (index) {
      case 0:
        return (
          <CompanyForm
            initialValues={companyInfo}
            onSubmit={(values) => {
              // Stash the values in the component state
              setCompanyInfo(values);
              // Move on to the next form
              nextStep();
            }}
            validator={validator}
          />
        );
      case 1:
        return (
          <ProfileForm
            initialValues={profileInfo}
            onSubmit={(values) => {
              // Stash the values on the local state, just in case
              setProfileInfo(values);
              // Form the full payload from the two forms
              const payload = {
                ...companyInfo,
                ...values,
              };
              // The API doesn't actually want the confirmPassword field
              delete payload.confirmPassword;
              const params = new URLSearchParams(window.location.search);
              payload.referral = params.get('referral') || '';
              setSubError(null);
              submitAccountData(payload)
                .then(() => nextStep()) // Success! Move on to the success step
                .catch((err) => {
                  // Trouble!
                  if (err.response.data.Code < 9000) {
                    setSubError(err.response.data.Message); // User visible message
                  } else {
                    setSubError('Something went wrong. Please try again.');
                    console.error('Server Error: ', err.response.data.Message);
                  }
                });
            }}
            onBack={(values) => {
              setProfileInfo(values);
              prevStep();
            }}
            validator={validator}
          />
        );
      case 2:
        return <AccountSetupSuccess />;
      default:
        return null;
    }
  }
  return (
    <DynamicThemeProvider themeName="cava">
      <GlobalStyle />
      <AccountSetupPage flexDirection={['column', 'column', 'column', 'row']}>
        <MatchMedia.XSmallOnly>
          <HeaderMobile subheadText="It's Easy to Get Started With AmTrav" homeUrl={amtravHome} />
        </MatchMedia.XSmallOnly>
        <MatchMedia.SmallOnly>
          <HeaderMobile subheadText="It's Easy to Get Started With AmTrav" homeUrl={amtravHome} />
        </MatchMedia.SmallOnly>
        <MatchMedia.MediumOnly>
          <HeaderMobile subheadText="It's Easy to Get Started With AmTrav" homeUrl={amtravHome} />
        </MatchMedia.MediumOnly>
        <MatchMedia.Large>
          <Flex flexDirection="column">
            <Sidebar
              marketingContent={<MarketingBubbles bubbleMessage={step === 0 ? bubbleMessage1 : bubbleMessage2} />}
              subheadMessage={<Subhead />}
              homeUrl={amtravHome}
            />
          </Flex>
        </MatchMedia.Large>
        <SignUpContent flexDirection="column">
          <MatchMedia.Large>
            <IntroMessage>{step < 2 ? introMessage1 : introMessage2}</IntroMessage>
          </MatchMedia.Large>
          <VisualStepsWrapper mt={['24px', '24px', '24px', 2]} mx="auto" pt={[3, 3, 3, 0]}>
            <WizardTracker steps={steps} activeStep={step} />
            <StepHeader step={step} />
          </VisualStepsWrapper>
          <Box p={[3, 3, 3, 0]} mt={[0, 0, 0, 2]} mb={[3, 3, 3, '70px']}>
            {subError ? (
              <Box mb={[3, 3, 3, 4]} mt={[0, 0, 0, '-16px']}>
                <Notice kind="danger">{subError}</Notice>
              </Box>
            ) : null}
            <FormWrapper>{selectForm(step)}</FormWrapper>
          </Box>
          <Flex px={[3, 3, 0]} flexDirection="column">
            <UserAgreementLinks />
            <CustomerServiceCTA />
          </Flex>
        </SignUpContent>
      </AccountSetupPage>
    </DynamicThemeProvider>
  );
};

export default AccountSetupApp;
